import { format } from 'date-fns';

import { useRef } from 'react';
import { PaymentOptions, T_AcquisitionForm } from 'types/acquisition-form.type';
import { FormattedAddress } from 'types/address-lookup.type';
import { ServiceStatus } from 'types/konakart.type';
import { VoucherDetails } from 'types/order.type';
import { BBPlanIndex, ConnectionType, DetailAddons, FibreCompanies } from 'types/prequal.type';

import { IS_CAPTCHA_ENABLED, boxCampaigns } from '@/lib/config';
import {
  BUNDLE,
  CATEGORY,
  COUPON_MAPPING_FOR_OFFER_TEXT,
  DISCOUNT_SKUS_LIST,
  DefaultICOMSPin,
  HARDWARE,
  MODAL_DEVICE_INFO_TYPE,
  PACKAGES_LIST,
  PhoneNumberType,
  ProductType,
  VoucherProductTypeEnums,
  accountType,
  countryCode,
  dateFormat,
  defaultTime,
  productSkuIds,
} from '@/lib/constants';
import { splitPhoneNumber, validatePhoneNumber } from '@/lib/validations';
import { addressService, sentryService, staticJsonConfigService } from '@/services/index';
import { AddressPrequalification } from '@/services/prequal/service';

import { matchAddress } from './nzPostAddress';
import { getSkyHomePhonePackages } from './productFilteredBasedOnCategory';
import { getDeviceType, hasDeviceType } from './productMapper';

type T_formdata = {
  paymentMethod: string;
  accountNumber?: string;
  bankName?: string;
  coupons: any;
  products: any;
  formData: any;
  customerId: any;
  productList: any;
  otTotalNoOtcPrice: number;
  isSelectedDevice: string;
  isBBAdded?: boolean;
  prequalData?: any;
  subTotalPriceItem?: number;
  isVoiceAdded?: boolean;
  category?: string;
  voucherDetails?: VoucherDetails;
  additionalDetails?: any;
};

const broadbandPlans: string[] = [
  productSkuIds.broadbandLightningFastWiFi.primary,
  productSkuIds.broadbandLightningFastWiFiBoost.primary,
  productSkuIds.broadbandWifi100.primary,
  productSkuIds.broadbandWifi100Boost.primary,
];

const buildContactNumber = (areaCode?: string, phoneNumber?: string) => {
  const normalizedAreaCode = `${areaCode ?? ''}`.trim();
  const normalizedPhoneNumber = `${phoneNumber ?? ''}`.trim();

  if (!normalizedAreaCode) return normalizedPhoneNumber;
  if (!normalizedPhoneNumber) return normalizedAreaCode;

  const parsedPhoneNumber = splitPhoneNumber(normalizedPhoneNumber);
  const isAlreadyCombined =
    normalizedPhoneNumber.startsWith(normalizedAreaCode) &&
    parsedPhoneNumber.areaCode === normalizedAreaCode &&
    validatePhoneNumber(normalizedPhoneNumber) === true;

  return isAlreadyCombined
    ? normalizedPhoneNumber
    : `${normalizedAreaCode}${normalizedPhoneNumber}`;
};

type Grecaptcha = {
  ready: (_callback: () => void) => void;
  execute: (_siteKey: string, _options: { action: string }) => Promise<string>;
  reset?: () => void;
};

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
    _recaptchaPromise?: Promise<Grecaptcha | null>;
  }
}

export const productCartPayload = (
  product: any,
  occurrenceNumber: string | null,
  category?: string
) => {
  const isTVBB = category && category.toLowerCase() === CATEGORY.TV_BROADBAND.toLowerCase();
  const updatePrice =
    isTVBB &&
    product.categories.toLowerCase() === CATEGORY.BROADBAND.toLowerCase() &&
    product.subCategories.toLowerCase() === 'bb-package'
      ? product.kkProductInfo.price1
      : product.kkProductInfo.price;

  const updatePriceIncTax =
    isTVBB &&
    product.categories.toLowerCase() === CATEGORY.BROADBAND.toLowerCase() &&
    product.subCategories.toLowerCase() === 'bb-package'
      ? updatePrice
      : product.kkProductInfo.priceIncTax;

  return {
    currentlySubscribed: false,
    downgrade: false,
    finalPriceIncTax: product?.kkProductInfo?.priceIncTax,
    inPromotion: false,
    isPhantom: false,
    occurrenceNumber: occurrenceNumber,
    productId: product.id,
    quantity: 1,
    title: product.title,
    category: product.categories,
    subCategory: product.subCategories,
    type: product.type,
    titleImageUrl: occurrenceNumber
      ? product?.thumbnail?.fields?.file?.url || null
      : product?.titleImageMobile?.fields?.file?.url || null,
    thumbnail: product?.thumbnail?.fields?.file?.url,
    desktopThumbnail: product?.titleImageDekstop?.fields?.file?.url || null,
    mobileThumbnail: product?.titleImageMobile?.fields?.file?.url || null,
    bulletInfoImage: product?.bulletInfoImage?.fields?.file?.url || null,
    price0: updatePrice,
    categoryId: product.kkProductInfo.categoryId,
    sku: product.kkProductInfo.sku,
    priceIncTax: updatePriceIncTax,
    price: product.kkProductInfo.actualPrice,
    name: product.kkProductInfo.name,
    image: product.kkProductInfo.image,
    custom9: product.kkProductInfo.custom9,
    custom1: product.kkProductInfo.custom1,
    isNewlyAddedProduct: product?.isNewlyAddedProduct ?? false,
    variant: product?.variant,
  };
};

export const checkAdditonalSkus = (
  category: string | null,
  skus: string,
  productList: any[]
): string => {
  if (!category || category.toLowerCase() === CATEGORY.TV.toLowerCase()) {
    return skus;
  }

  const inputSkus = skus
    .split('-')
    .map(sku => sku.trim())
    .filter(Boolean);
  const additionalSkusSet = new Set<string>(inputSkus); // Start with original SKUs

  for (const inputSku of inputSkus) {
    const product = productList.find(item => item?.kkProductInfo?.sku === inputSku);

    if (product?.productKkInfo?.AdditionalSKU) {
      const skusFromProduct = product.productKkInfo.AdditionalSKU.split(',')
        .map((s: string) => s.trim())
        .filter(Boolean);

      skusFromProduct.forEach((sku: string) => additionalSkusSet.add(sku));
    }
  }

  return Array.from(additionalSkusSet).join('-');
};

export const mapSkusToCartItems = (
  skus: string,
  productList: any[],
  occurrenceNumber: string | null,
  category: string,
  categoryType: string
) => {
  const skudList = checkAdditonalSkus(category, skus, productList);
  return skudList
    .split('-')
    .map((sku: string) => {
      const product = productList.find(
        (p: any) => p.kkProductInfo.sku === sku && p.type !== BUNDLE
      );
      if (!product) return null;

      const isOccurrenceNumberRequired = sku.includes('BB');
      return productCartPayload(
        product,
        isOccurrenceNumberRequired ? null : occurrenceNumber,
        categoryType
      );
    })
    .filter(Boolean);
};

export const deviceExistFromCart = (deviceList: any[], cartProductList: any[], item: any) => {
  if (!deviceList.length) return;

  let deviceItem = deviceList.find(
    (it: any) =>
      it.id !== item.id && it.categories.toLocaleLowerCase() === item.categories.toLocaleLowerCase()
  );

  return deviceItem && cartProductList.find((it: any) => it.productId === deviceItem.id);
};

export const getDiscountPrice = (finalPriceIncTax: number, totalPrice: number) => {
  let difference = finalPriceIncTax - totalPrice;
  return difference > 0;
};

export const offerInformationTextBasedOnCoupon = (
  orderProducts: any,
  offer: any,
  productList: any,
  isPriceIncressComms: any
) => {
  if (!offer?.couponCode) return;
  let offerText: string = '';
  switch (offer.couponCode) {
    case COUPON_MAPPING_FOR_OFFER_TEXT[0].couponCode:
      // Device Offfer Text

      const deviceProductList = orderProducts.filter(
        (item: any) =>
          item.type === HARDWARE &&
          [PACKAGES_LIST.TV_BOX.toLowerCase(), PACKAGES_LIST.TV_POD.toLowerCase()].includes(
            item.subCategory.toLowerCase()
          ) &&
          item.category.toLocaleLowerCase() === CATEGORY.TV.toLocaleLowerCase()
      );

      let deviceItem = null;
      if (deviceProductList.length > 0) {
        const targetSku = deviceProductList[0].sku;
        deviceItem = productList.find((it: any) => it.kkProductInfo?.sku === targetSku);
      }

      if (deviceProductList.length > 0 && deviceItem) {
        offerText = `${deviceProductList[0].name} on us (usually $${deviceItem.price})`;
      }
      break;
    case COUPON_MAPPING_FOR_OFFER_TEXT[1].couponCode[0]:
    case COUPON_MAPPING_FOR_OFFER_TEXT[1].couponCode[1]:
      // TV packages
      const packageProductList = orderProducts.filter(
        (item: any) =>
          item.occurrenceNumber === null &&
          item.category.toLocaleLowerCase() === CATEGORY.TV.toLocaleLowerCase() &&
          item.sku !== productSkuIds?.movies?.primary
      );
      let result = '';
      if (packageProductList.length > 0) {
        const packagesWithPrices = packageProductList.map(
          (it: any) => `${it.title} is $${it.finalPriceIncTax.toFixed(2)}/month`
        );

        if (packagesWithPrices.length === 1) {
          result = packagesWithPrices[0];
        } else if (packagesWithPrices.length === 2) {
          result = `${packagesWithPrices[0]} and ${packagesWithPrices[1]}`;
        } else {
          result = `${packagesWithPrices.slice(0, -1).join(', ')}, and ${
            packagesWithPrices[packagesWithPrices.length - 1]
          }`;
        }
      }
      offerText = `After offer ends, ${result}. T&Cs apply.`;
      offerText = isPriceIncressComms
        ? offerText.replace('$47.00/month', () => '$52.00/month')
        : offerText;
      break;
    case COUPON_MAPPING_FOR_OFFER_TEXT[2].couponCode: {
      // Moives package
      const productItem = orderProducts.filter(
        (item: any) => item.productId.toString() === offer.custom1.toString()
      );
      const price = productItem.length && productItem[0]?.finalPriceIncTax;
      offerText = `After offer ends, Sky Movies is $${price.toFixed(2)}/month. T&Cs apply.`;
      break;
    }

    default:
      offerText = offer?.description;
      break;
  }
  return offerText;
};

const waitForRecaptchaReady = (recaptcha: Grecaptcha): Promise<Grecaptcha> =>
  new Promise(resolve => {
    recaptcha.ready(() => resolve(recaptcha));
  });

const resolveRecaptchaFromWindow = (
  windowRef: Window,
  resolve: (value: Grecaptcha | null) => void
) => {
  const grecaptcha = windowRef.grecaptcha;
  if (!grecaptcha) {
    resolve(null);
    return;
  }

  waitForRecaptchaReady(grecaptcha).then(resolve);
};

const createRecaptchaPromise = (siteKey: string, windowRef: Window): Promise<Grecaptcha | null> =>
  new Promise((resolve, reject) => {
    const scriptId = 'recaptcha-script';
    const existing = document.getElementById(scriptId) as HTMLScriptElement | null;

    const handleLoad = () => resolveRecaptchaFromWindow(windowRef, resolve);
    const handleError = () => reject(new Error('Recaptcha failed to load'));

    if (existing) {
      existing.addEventListener('load', handleLoad, { once: true });
      existing.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.defer = true;
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.onload = handleLoad;
    script.onerror = handleError;
    document.head.appendChild(script);
  });

const loadRecaptcha = async (siteKey: string): Promise<Grecaptcha | null> => {
  if (globalThis.window === undefined) return null;
  if (!siteKey) return null;

  const browserWindow = globalThis.window;
  const existingRecaptcha = browserWindow.grecaptcha;
  if (existingRecaptcha) {
    return waitForRecaptchaReady(existingRecaptcha);
  }

  if (browserWindow._recaptchaPromise) {
    return browserWindow._recaptchaPromise;
  }

  browserWindow._recaptchaPromise = createRecaptchaPromise(siteKey, browserWindow);
  return browserWindow._recaptchaPromise;
};

export const useCaptcha = () => {
  const captchaRef = useRef<Grecaptcha | null>(null);
  const siteKey = process.env.REACT_APP_CAPTCHA_KEY ?? '';

  const generateCaptchaToken = async (): Promise<string> => {
    // Return mock token during tests
    if (process.env.NODE_ENV === 'test') {
      return `test-captcha-${Date.now()}`;
    }

    let grecaptcha: Grecaptcha | null;
    try {
      grecaptcha = await loadRecaptcha(siteKey);
    } catch (err) {
      sentryService.captureException(err);
      return '';
    }
    if (!grecaptcha) return '';

    captchaRef.current = grecaptcha;

    try {
      const token = await grecaptcha.execute(siteKey, {
        action: 'submit',
      });
      return token || '';
    } catch (err) {
      sentryService.captureException(err);
      return '';
    }
  };

  return { captchaRef, generateCaptchaToken };
};

export const voucherDetails = (voucher: any) => {
  const voucherDetails: VoucherDetails = {
    skus: voucher?.voucher?.ServiceCodes!,
    coupons: voucher?.voucher?.Campaigns!,
    description: voucher?.voucher?.OfferDescription!,
    voucherCode: voucher?.voucherCode!,
    url: voucher?.voucher?.url ?? '',
  };
  return voucherDetails;
};
export const getNewOrderFormdata = async (
  data: T_formdata,
  generateCaptchaToken: () => Promise<string>
) => {
  const orderseriveAddress = await addressService.getServiceAddress(data.formData.serviceAddress);

  // deliveryAddress is needed only if cart has pod or broadband (router) devices
  const { skyPod, bbDevice } = hasDeviceType(data.products);
  const hasHardwareDelivery = skyPod() || bbDevice();
  const deliveryAddress = hasHardwareDelivery
    ? await getDeliveryInformation(orderseriveAddress[1], data.formData)
    : null;

  let products: any = [];
  //filtering tv products from orderstore includign devices
  let tvProducts = data?.products?.filter((prdt: any) =>
    prdt.category.toLocaleLowerCase().includes(CATEGORY.TV.toLocaleLowerCase())
  );
  if (shouldBuildBroadbandOrder(data)) {
    const broadbandProducts = await buildBroadbandAndVoiceProducts(data, deliveryAddress);
    products.push(...broadbandProducts);
  }

  if (shouldBuildTvOrder(data)) {
    const tvOrderProduct = await buildTvProducts(data, tvProducts, deliveryAddress);
    products = [...products, tvOrderProduct];
  }

  const token = IS_CAPTCHA_ENABLED ? await generateCaptchaToken() : '';
  let date = data?.formData?.dateOfBirth ? new Date(data?.formData?.dateOfBirth) : '';
  let formattedDOB = date ? format(date, dateFormat) : undefined;
  const formPayment = buildFormPayment(data.formData);
  const orderProducts = buildOrderProductsPayload(data.products);
  const phoneNumber = buildPhoneNumbers(data.formData);
  const serviceAddress = {
    ...orderseriveAddress[0],
    city: orderseriveAddress[0].city.slice(0, 25),
  };
  let formdata = {
    order: {
      couponDetails: data.coupons,
      customerId: data.customerId,
      freeProduct: [],
      orderProducts: orderProducts,
      orderTotals: [
        {
          className: 'ot_total_no_otc',
          value: data?.otTotalNoOtcPrice,
        },
        {
          className: 'ot_subtotal',
          value: data?.subTotalPriceItem,
        },
      ],
    },
    customerDetails: {
      billViaEmail: 'true',
      marketingEmails: data.formData.abandonedCartAck ?? data.formData.consent ?? false,
      ...formPayment,
      phoneNumbers: phoneNumber,
      firstName: data.formData.firstName,
      lastName: data.formData.lastName,
      dateOfBirth: formattedDOB,
      icomsPin: DefaultICOMSPin,
      serviceAddress,
      email: data.formData.email,
      houseOwnership: data.formData.residencyStatus === 0 ? 'true' : 'false',
      ...data.additionalDetails,
    },
    orderDetails: {
      products: products,
      voucherCode: data?.voucherDetails?.voucherCode ?? undefined,
    },
    token: token,
    useNewOrderEndpoint: false,
    eligibilityId: '',
    voucherDetails: data?.voucherDetails ? voucherDetails(data?.voucherDetails) : undefined,
  };
  return formdata;
};

const shouldBuildBroadbandOrder = (data: T_formdata) =>
  data?.isBBAdded ||
  data?.category?.toLocaleLowerCase() === CATEGORY.TV_BROADBAND.toLocaleLowerCase();

const shouldBuildTvOrder = (data: T_formdata) =>
  data?.category?.toLocaleLowerCase() === CATEGORY.TV.toLocaleLowerCase() ||
  data?.category?.toLocaleLowerCase() === CATEGORY.TV_BROADBAND.toLocaleLowerCase();

const buildOrderProductsPayload = (products: any[]) =>
  products.map((prod: any) => ({
    ...prod,
    product: {
      categoryId: prod.categoryId,
      sku: prod.sku,
      price0: prod.price0,
      name: prod.name,
      image: prod.image,
      price: prod.price,
      priceIncTax: prod.priceIncTax,
      custom9: prod.custom9,
      custom1: prod.custom1,
    },
  }));

const buildFormPayment = (formData: T_formdata['formData']) => {
  if (formData.paymentMethod === PaymentOptions.CREDIT_CARD) {
    return {
      creditCardPayment: true,
    };
  }

  return {
    creditCardPayment: false,
    directDebit: {
      accountType: accountType,
      bankAccount: formData.accountNumber?.replace(/-/g, ''),
      accountHolder: formData.firstName,
    },
  };
};

const getLocalPhoneNumber = (formData: T_formdata['formData']) => {
  const parsedPhoneNumber = splitPhoneNumber(formData.phoneNumber ?? '');

  if (parsedPhoneNumber.kind && parsedPhoneNumber.localNumber) {
    return parsedPhoneNumber.localNumber;
  }

  const phoneNumber = formData.phoneNumber ?? '';
  const areaCode = formData.areaCode ?? '';

  return phoneNumber.startsWith(areaCode) ? phoneNumber.slice(areaCode.length) : phoneNumber;
};

const buildPhoneNumbers = (formData: T_formdata['formData']) => [
  {
    type: PhoneNumberType,
    phoneNumber: {
      countryCode: countryCode,
      areaCode: formData.areaCode,
      number: getLocalPhoneNumber(formData),
    },
  },
];

const buildBroadbandAndVoiceProducts = async (data: T_formdata, deliveryAddress: any) => {
  let products: any[] = [];
  let bbProducts = data?.products?.filter(
    (prdt: any) =>
      prdt.category.toLocaleLowerCase().includes(CATEGORY.BROADBAND.toLocaleLowerCase()) ||
      DISCOUNT_SKUS_LIST.includes(prdt.sku)
  );
  let voiceProducts: any[] = [];

  if (data?.isVoiceAdded) {
    const filteredBBProducts = filterHomeAndBBProducts(bbProducts);
    bbProducts = filteredBBProducts?.[0] ?? [];
    voiceProducts = filteredBBProducts?.[1] ?? [];
  }

  let serviceSkus = createServiceSkus(
    data.productList,
    bbProducts,
    data?.category?.toLocaleLowerCase() === CATEGORY.TV_BROADBAND.toLocaleLowerCase()
  );

  const dataDetails = await createDataRequest(
    bbProducts,
    data.coupons,
    serviceSkus,
    data.formData,
    deliveryAddress,
    data.prequalData,
    data?.isVoiceAdded ?? false,
    data?.category ?? ''
  );
  products.push(dataDetails);

  if (!data?.isVoiceAdded) {
    return products;
  }

  serviceSkus = createServiceSkus(data.productList, voiceProducts);
  const voiceDetails = await createVoiceRequest(
    voiceProducts,
    data.coupons,
    serviceSkus,
    data.formData,
    deliveryAddress,
    data.prequalData,
    data.productList,
    data?.category ?? '',
    bbProducts
  );
  products.push(voiceDetails);

  return products;
};

const buildTvProducts = async (data: T_formdata, tvProducts: any[], deliveryAddress: any) => {
  const serviceSkus = createTvServiceSkus(data.productList, tvProducts, data.voucherDetails);
  const deviceList = tvProducts?.filter(
    (it: any) =>
      it?.type === HARDWARE && it?.category?.toLocaleLowerCase() === CATEGORY.TV.toLocaleLowerCase()
  );
  const firstDevice = deviceList?.[0];
  const deviceType = getDeviceType(firstDevice?.sku || '') ?? '';
  const orderDetails = await createOrderDetails(
    data.coupons,
    serviceSkus,
    data.formData,
    deliveryAddress,
    deviceType
  );

  return orderDetails.products[0];
};

export const getDeliveryInfo = (formData: T_AcquisitionForm, isBB: boolean) => {
  let phoneNumber = buildContactNumber(formData?.areaCode, formData?.phoneNumber);
  if (isBB) {
    phoneNumber = buildContactNumber(formData?.broadbandAreaCode, formData?.broadbandPhoneNumber);
  }
  return {
    contactName: `${formData?.firstName ?? ''} ${formData?.lastName ?? ''}`.trim(),
    contactNumber: phoneNumber,
    contactEmail: `${formData?.email ?? ''}`.trim(),
    notes: formData.deliveryInstructions?.trim() ?? '',
  };
};

// export const getAddressDetails = async (formData: T_AcquisitionForm) => {
//   let address: {
//     serviceAddress?: OrderServiceAddress;
//     deliveryAddress?: FormattedAddress;
//   } = {};
//   const useAsDeliveryAddress = formData?.useAsDeliveryAddress;
//   const [primaryServiceAddress, primaryFormattedAddress] = await addressService.getServiceAddress(
//     formData?.serviceAddress,
//   );
//   address.serviceAddress = primaryServiceAddress;
//   if (!useAsDeliveryAddress) {
//     const deliveryFormattedAddress = await addressService.getServiceAddress(formData?.deliveryAddress!);
//     address.deliveryAddress = deliveryFormattedAddress?.[1];
//   } else {
//     address.deliveryAddress = primaryFormattedAddress;
//   }
//   return address;
// };
export const bbPlanIndex = (bbPackages: any[]): BBPlanIndex => {
  let index = 2; //starter
  (bbPackages ?? [])?.forEach((b: any) => {
    if (
      [
        productSkuIds.broadbandLightningFastWiFiBoost.primary,
        productSkuIds.broadbandLightningFastWiFi.primary,
      ].includes(b?.sku)
    )
      index = 0;
    if (
      [
        productSkuIds.broadbandWifi100.primary,
        productSkuIds.broadbandWifi100Boost.primary,
      ].includes(b?.sku)
    )
      index = 1;
  });
  return index as BBPlanIndex;
};
export const getSiteInfo = (formData: T_AcquisitionForm, notes?: string) => {
  let siteContactName = '';

  const fullName = `${formData?.firstName ?? ''} ${formData?.lastName ?? ''}`.trim(); // Backtick for chrome `
  siteContactName = fullName.replace(/[0-9]/g, '');

  // Same for other fields, use user entered value, otherwise use a default
  return {
    siteContactName,
    siteContactNumber: buildContactNumber(
      formData?.broadbandAreaCode,
      formData?.broadbandPhoneNumber
    ),
    siteContactEmail: formData?.email,
    siteAccessInformation: notes ? notes : 'n/a',
  };
};
export const getDetailAddOns = async (
  hasModem: boolean,
  hasMesh: boolean,
  hasStaticIp: boolean,
  prequalData: AddressPrequalification | undefined
): Promise<DetailAddons[]> => {
  let addOns: DetailAddons[] = [];

  if (hasModem) {
    //locate the modem model -- it is the router that has no other part
    const productId = prequalData?.getModemModel();
    // Add modem
    addOns.push({
      type: 'modem',
      productId: productId!,
    });
  }

  if (hasMesh) {
    const meshproductId = prequalData?.getSingleMeshDeviceModel();

    // Add single mesh device
    addOns.push({
      type: 'mesh',
      productId: meshproductId!,
    });
  }

  if (hasStaticIp) {
    const staticIpProductID = prequalData?.getStaticIPProductKey();

    // Add static_ip
    addOns.push({
      type: 'static_ip',
      productId: 'static_ip',
      ratePlan: staticIpProductID ? staticIpProductID : 'Sky_static_ip',
    });
  }
  return addOns;
};

// const getvoiceProviderUserId = (voiceDetails: T_AcquisitionForm | undefined): string | undefined => {
//   if (!voiceDetails) return undefined;
//   if (
//     voiceDetails.isUsingExistingPhone === 'No' ||
//     (voiceDetails.isUsingExistingPhone === 'Yes' && voiceDetails.isKeepExistingPhoneNumber === 'No')
//   ) {
//     return voiceDetails?.phoneNumberFromList;
//   } else return voiceDetails.phoneNumberAreaCode + voiceDetails.phoneNumberLineNumber;
// };

export const getAddons = (
  prequal: AddressPrequalification | undefined,
  orderProducts: any
): any => {
  // forcefuly tell its returning strings without undefined
  const keyArray: string[] = orderProducts
    .map((op: any) => op?.kkProductInfo?.model)
    .filter((item: any): item is string => !!item);
  const addons = prequal?.getRespectiveAddons(keyArray);
  // explicit addons for voice are type plans but endpoint has no type field
  const mappedAddons =
    addons?.map(x => ({
      productId: x.key,
      ratePlan: x.planDetails?.length ? x.planDetails[0].key : '',
      type: 'plan',
    })) ?? [];
  return mappedAddons;
};
export const getBBInstallType = (bbPackages: any) => {
  const isSelfInstall = !!bbPackages?.find(
    (b: any) => b?.sku === productSkuIds.broadbandSelfInstall.primary
  );
  const isByod = !!bbPackages?.find(
    (b: any) => b?.sku === productSkuIds.broadbandBringYourOwnDevice.primary
  );
  let installType = isSelfInstall
    ? productSkuIds.broadbandSelfInstall.label
    : isByod
      ? productSkuIds.broadbandBringYourOwnDevice.label
      : '';

  return installType;
};
export const getConnectionType = (
  prequalData: AddressPrequalification | undefined
): ConnectionType =>
  prequalData?.isNewFibreJourney()
    ? 'New'
    : prequalData?.isIntactONTJourney()
      ? 'Connect'
      : 'Migrate';

export const getLFC = (prequalData: AddressPrequalification | undefined) => {
  if (!prequalData) {
    return undefined;
  }

  switch (prequalData.getLFC()) {
    case FibreCompanies.Chorus:
      return 'chorus';
    case FibreCompanies.Enable:
      return 'enable';
    case FibreCompanies.NorthPower:
      return 'northpower';
    case FibreCompanies.UFF:
      return 'uff';
    default:
      return undefined;
  }
};
export const checkBBAddons = (BBpackages: any[]) => {
  return {
    hasPerfectInstall: !!BBpackages?.find(
      (b: any) => b?.sku === productSkuIds.broadbandPerfectInstall.primary
    ),
    hasModem: !!BBpackages?.find((b: any) => b?.sku === productSkuIds.skyRouter.primary),
    hasMesh: !!BBpackages?.find((b: any) => b?.sku === productSkuIds.meshDevice.primary),
    hasStaticIp: !!BBpackages?.find((b: any) => b?.sku === productSkuIds.broadbandStaticIP.primary),
  };
};

export const getLSP = (
  prequalData: AddressPrequalification | undefined,
  details: T_AcquisitionForm
) => {
  if (prequalData?.isIntactONTJourney() || prequalData?.isNewFibreJourney()) {
    return {};
  }

  return {
    serviceProvider: details.currentServiceDropDown,
    accountName: '',
    accountNumber:
      details.providerAccountNumber && details.providerAccountNumber !== ''
        ? details.providerAccountNumber
        : '364782822', //ask jay
  };
};

export const createPrequalDetails = async (
  BBpackages: any[],
  formData: T_AcquisitionForm,
  prequalData: AddressPrequalification,
  isVoiceAdded: boolean
) => {
  const bbIndex = bbPlanIndex(BBpackages);
  const { hasMesh, hasModem, hasPerfectInstall, hasStaticIp } = checkBBAddons(BBpackages);

  return {
    addonOnly: false,
    hardwareOnly: false,
    productId: prequalData?.getProductKey(bbIndex, isVoiceAdded),
    // @ts-ignore
    activationDate: getActivationDate(formData, prequalData),
    locationId: prequalData?.getLocationId() ?? '',
    connectionType: getConnectionType(prequalData),
    broadbandProduct: prequalData?.getProduct(bbIndex, isVoiceAdded),
    activationTime: getActivationTime(formData, prequalData),
    siteInfo: getSiteInfo(formData),
    installType: getBBInstallType(BBpackages),
    losingServiceProviderDetails: getLSP(prequalData, formData),
    addons: await getDetailAddOns(hasModem, hasMesh, hasStaticIp, prequalData),
    skyTech: hasPerfectInstall,
    // @ts-ignore
    consentRequired: formData?.changeProviderAck ?? false,
    lfc: getLFC(prequalData),
  };
};

const buildLosingServiceProviderDetails = (formData: any): any => {
  // We only care about losing service provider if its porting in existing number (want existing number)
  // That occurs only if we have existing number, and want to keep it
  if (
    formData &&
    formData.isUsingExistingPhone === 'Yes' &&
    formData.isKeepExistingPhoneNumber === 'Yes'
  ) {
    let serviceProvider = undefined;
    // if the providers are matching for BB and Voice
    if (formData.isPhoneProviderMatchBroadband === 'Yes' && formData.currentServiceProvider) {
      // Use the explicit input field if value is other, otherwise use the dropdown field value
      // const serviceProviderBroadband =
      serviceProvider =
        formData.currentServiceDropDown !== 'Other'
          ? formData.currentServiceDropDown
          : formData.otherServiceProvider;
    } else {
      // If the provider is different to the broadband provider, check whether it is the 'Other' option.
      // Use the dropdown and if its the 'Other' option use that field as service provider
      // const serviceProviderVoice =
      serviceProvider =
        formData.currentProviderDropDown !== 'Other'
          ? formData.currentProviderDropDown
          : formData.otherProvider; //check with priyanga the validation
    }

    // account number also incorporates the BB logic
    return {
      existingPhoneNumber: `${formData.phoneNumberAreaCode}${formData.phoneNumberLineNumber}`,
      accountNumber: formData.accountNumberForHomePhone,
      portIn: true,
      serviceProvider: serviceProvider,
    };
  }
  return undefined;
};

const getNewPhoneNumber = (voiceDetails: any | undefined): string | undefined => {
  // unable to know correctly if the number selected means that they want a new number
  // if a number is selected from the phone number list and
  //  (using existing number and is going to not keep the same number OR
  //  does not have an existing number)
  return voiceDetails &&
    voiceDetails.phoneNumberFromList &&
    ((voiceDetails.isUsingExistingPhone === 'Yes' &&
      voiceDetails.isKeepExistingPhoneNumber === 'No') ||
      voiceDetails.isUsingExistingPhone === 'No')
    ? voiceDetails.phoneNumberFromList
    : undefined;
};

const formatDateValue = (value: unknown): string | null => {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value as string);

  const startOfDay = (dateValue: Date) =>
    new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());

  if (Number.isNaN(date.getTime()) || startOfDay(date) < startOfDay(new Date())) {
    return null;
  }

  return format(date, dateFormat);
};

const getActivationDate = (details: any, prequalData: AddressPrequalification | undefined) => {
  if (
    prequalData?.consentRequired() ||
    (prequalData?.isNewFibreJourney() && prequalData?.isLfcDoesntTellUsIfConsentRequired())
  ) {
    return null;
  }
  // Return installation date for activation date if it's a chorus new fibre journey where consent is not required.
  if (
    prequalData?.isNewFibreJourney() &&
    prequalData?.isChorus() &&
    !prequalData?.consentRequired()
  ) {
    return formatDateValue(details?.installationDate ?? details?.prefferedInstallationDate);
  }
  return formatDateValue(details?.prefferedInstallationDate);
};

const getActivationTime = (details: any, prequalData: AddressPrequalification | undefined) => {
  if (
    prequalData?.consentRequired() ||
    (prequalData?.isNewFibreJourney() && prequalData?.isLfcDoesntTellUsIfConsentRequired())
  ) {
    return null;
  }
  if (prequalData?.isNewFibreJourney() && details.installationTime === defaultTime) {
    return '13:00';
  }
  return '09:00';
};

export const createVoicePrequalDetails = async (
  voiceProducts: any[],
  formData: T_AcquisitionForm,
  prequalData: AddressPrequalification,
  productList: any,
  BBpackages: any
) => {
  const bbIndex = isOneGigBroadbandPlan(false, {}, voiceProducts);
  let orderProducts = productList.filter((item: any) =>
    voiceProducts.some((sub: any) => sub?.sku === item?.productSku?.[0])
  );
  return {
    productId: prequalData?.getProductKey(bbIndex, true), //value need to check nilesh
    // @ts-ignore
    broadbandProduct: prequalData?.getProduct(bbIndex, true),
    newPhoneNumber: getNewPhoneNumber(formData),
    losingServiceProviderDetails: buildLosingServiceProviderDetails(formData),
    addons: await getAddons(prequalData, orderProducts),
  };
};

export const getDeliveryInformation = async (deliveryDetails: any, customer: any) => {
  let deliveryAddress = deliveryDetails;

  try {
    let deliveryAddressId = customer.deliveryAddress?.id ?? '';
    // If no adderssId find the addressId (nzPost addressID)
    if (!deliveryAddressId) {
      const { line1, line2, line3, line4 } = deliveryDetails;
      const serviceAddress = [line1, line2, line3, line4].toString();
      const nzAddress = await addressService.searchNzPostAddress({
        query: serviceAddress,
        count: 1,
      });
      // if no address found return the service address as delivery address
      if (!nzAddress || nzAddress.length < 1) return deliveryAddress;

      // therse are the abbreviations that are used to normalize service address and nzpost address to match them. e.g. street = st, avenue = ave, etc.
      const abbreviations = await staticJsonConfigService.staticJson().then(res => {
        return res.find(json => json.contentId === 'address-abbreviations')?.content as
          | Record<string, string>
          | undefined;
      });

      const { address_id, address } = nzAddress[0];
      const isAddressMatched = matchAddress(serviceAddress, address, abbreviations);
      if (isAddressMatched) deliveryAddressId = address_id; // if no addressId we will use the service address as delivery address
    }

    if (deliveryAddressId) {
      const nzAddress = await addressService.getNzPostAddressDetails(deliveryAddressId.toString());
      const { street_number, street, streetType, suburb, city, postalCode, address_id } = nzAddress;
      deliveryAddress = {
        line1: `${street_number} ${street} ${streetType}`,
        line2: suburb ?? '',
        line3: city ?? '',
        line4: postalCode ?? '',
        tui: '',
        addressId: address_id,
      };
    }
  } catch (error) {
    sentryService.captureException(error);
    return deliveryAddress; // Return the original service address if an error occurs
  }
  return deliveryAddress;
};

export const createDataRequest = async (
  bbProducts: any,
  coupons: any,
  addSkus: any,
  formData: T_AcquisitionForm,
  deliveryAddress: FormattedAddress,
  prequalData: AddressPrequalification,
  isVoiceAdded: boolean,
  category: string
) => {
  const products = bbProducts;

  const serviceDetails = await createPrequalDetails(products, formData, prequalData, isVoiceAdded);
  let campaigns: any = [];
  coupons.forEach((coupon: any) => {
    let campainObj = {};
    if (coupon?.custom5?.toLocaleLowerCase() === CATEGORY.BROADBAND.toLocaleLowerCase()) {
      campainObj = {
        id: coupon.couponCode,
        description: coupon.description,
        boxCampaign: boxCampaigns.includes(coupon?.couponCode),
      };
    }
    if (Object.keys(campainObj)?.length > 0) campaigns = [...campaigns, campainObj];
  });
  // const deliveryAdd = {...deliveryAddress, deliveryInfo: getDeliveryInfo(formData)}
  return {
    type: ProductType.Data,
    campaigns: campaigns,
    services: [
      {
        actions: [{ add: addSkus.addSkus, remove: [] }],
        details: [
          {
            ...serviceDetails,
            deliveryAddress: deliveryAddress
              ? { ...deliveryAddress, deliveryInfo: getDeliveryInfo(formData, true) }
              : null,
          },
        ],
        installDate: serviceDetails?.skyTech
          ? (formatDateValue(formData!.prefferedInstallationDate) ?? '')
          : '',
        installTime: serviceDetails?.skyTech ? defaultTime : '',
      },
    ],
  };
};

export const createVoiceRequest = async (
  voiceProducts: any,
  coupons: any,
  addSkus: any,
  formData: T_AcquisitionForm,
  deliveryAddress: FormattedAddress,
  prequalData: AddressPrequalification,
  productList: any,
  category: string,
  bbProducts?: any
) => {
  const products = voiceProducts;

  const serviceDetails = await createVoicePrequalDetails(
    products,
    formData,
    prequalData,
    productList,
    bbProducts
  );
  let campaigns: any = [];
  coupons.forEach((coupon: any) => {
    let campainObj: any = {};
    if (coupon?.custom5?.toLocaleLowerCase() === CATEGORY.BROADBAND.toLocaleLowerCase()) {
      campainObj = {
        id: coupon.couponCode,
        description: coupon.description,
        boxCampaign: boxCampaigns.includes(coupon?.couponCode),
      };
    }
    if (Object.keys(campainObj).length > 0) campaigns = [...campaigns, campainObj];
  });
  return {
    type: ProductType.Voice,
    campaigns: campaigns,
    services: [
      {
        actions: [{ add: addSkus.addSkus, remove: [] }],
        details: [
          {
            ...serviceDetails,
            // deliveryAddress: { ...deliveryAddress, deliveryInfo: getDeliveryInfo(formData) },
          },
        ],
        installDate: '',
        installTime: '',
      },
    ],
  };
};
const createOrderDetails = async (
  coupons: any,
  addSkus: any,
  formData: any,
  deliveryAddress: any,
  selecetdDevice: string
) => {
  let campaigns: any = [];
  // eslint-disable-next-line array-callback-return
  coupons.map((coupon: any) => {
    let campainObj = {};
    if (coupon?.custom5?.toLocaleLowerCase() === ProductType.Cable.toLocaleLowerCase()) {
      campainObj = {
        id: coupon.couponCode,
        description: coupon.description,
        boxCampaign: boxCampaigns.includes(coupon?.couponCode),
      };
    }
    if (Object.keys(campainObj).length > 0) campaigns = [...campaigns, campainObj];
  });

  let deliveryDetails: any = deliveryAddress
    ? { ...deliveryAddress, deliveryInfo: getDeliveryInfo(formData, false) }
    : null;
  let productObj = {
    type: ProductType.Cable,
    campaigns: campaigns,
    services: [
      {
        actions: [
          {
            add: addSkus.addSkus,
          },
        ],
        details: [],
        deliveryDetails: deliveryDetails,
      },
    ],
    installType: addSkus.installationType,
    installDate: formData.installationDate
      ? format(new Date(formData.installationDate), dateFormat)
      : '',
    installTime: formData.installationCode,
  };
  return { products: [productObj] };
};
const createTvServiceSkus = (products: any, cartTvProducts: any, voucher: any) => {
  let addSkus: Array<string> = [];
  let installationType = 'SELF';
  products
    .filter((item: any) => cartTvProducts.some((prdt: any) => prdt.productId === item.id))
    .forEach((product: any) => {
      if (product?.type === HARDWARE && product.productSku[0] === MODAL_DEVICE_INFO_TYPE.SKY_BOX)
        installationType = 'TECH'; //ask priyanga
      //console.log('chkkk777', addSkus, product?.productSku, product?.productKkInfo?.APISKU);
      const apiSku = product?.productKkInfo?.APISKU;
      addSkus = [...addSkus, ...(product?.productSku ?? []), ...(apiSku ? apiSku.split(',') : [])];
    });
  if (voucher?.voucher?.ServiceCodes) {
    // Filter out serviceCodes that are already in addSkus
    const newServiceCodes = voucher?.voucher?.ServiceCodes.filter(
      (serviceCode: string) => !addSkus.includes(serviceCode)
    );
    // Add the filtered service codes to addSkus
    addSkus = [...addSkus, ...newServiceCodes];
  }
  return { addSkus, installationType };
};
const filterHomeAndBBProducts = (products: any) => {
  let homeProducts = getSkyHomePhonePackages(products);
  let BBProducts = products.filter(
    (item: any) => !homeProducts.some((sub: any) => sub.categoryId === item.categoryId)
  );
  return [BBProducts, homeProducts];
};

export function isOneGigBroadbandPlan(
  hasActiveBroadbandPackage: boolean | undefined,
  billingServices: any,
  orderPackages: any
) {
  let currentBroadbandSubscription: string | undefined = undefined;

  if (hasActiveBroadbandPackage) {
    currentBroadbandSubscription = billingServices?.DataServiceOccurrence.flatMap(
      (occ: any) => occ.serviceDetails
    )?.find(
      (sd: any) =>
        sd.serviceStatus === ServiceStatus.Active &&
        sd.quantity > 0 &&
        broadbandPlans.includes(sd.serviceCode)
    )?.serviceCode;
  }

  let newBroadbandPackage = orderPackages.find((op: any) => broadbandPlans.includes(op.sku))?.sku;
  const packageToCheck = newBroadbandPackage ?? currentBroadbandSubscription;

  let bbPlanIndex: BBPlanIndex = 0;
  if (
    packageToCheck === productSkuIds.broadbandLightningFastWiFi.primary ||
    packageToCheck === productSkuIds.broadbandLightningFastWiFiBoost.primary
  ) {
    bbPlanIndex = 0;
  } else if (
    packageToCheck === productSkuIds.broadbandWifi100.primary ||
    packageToCheck === productSkuIds.broadbandWifi100Boost.primary
  ) {
    bbPlanIndex = 1;
  } else if (
    packageToCheck === productSkuIds.broadbandEssnFibre.primary ||
    packageToCheck === productSkuIds.broadbandEssnFibBoost.primary
  ) {
    bbPlanIndex = 2;
  }

  return bbPlanIndex;
}

const isBYOD = (products: any) => {
  if (getBBInstallType(products) === productSkuIds.broadbandBringYourOwnDevice.label) return true;
  else return false;
};
const createServiceSkus = (products: any, cartProducts: any, isTVBB?: boolean) => {
  let addSkus: Array<string> = [];
  let installationType = 'SELF';
  let isByod = isBYOD(cartProducts);
  //console.log('check33', products, cartProducts);
  products
    .filter((item: any) => cartProducts.some((prdt: any) => prdt.productId === item.id))
    .forEach((product: any) => {
      //console.log('check2', product);
      let prodKkInfo = product?.productKkInfo;
      let apiSkus = [];
      if (prodKkInfo) {
        apiSkus =
          prodKkInfo?.APISKU && prodKkInfo?.APISKU !== '' ? prodKkInfo?.APISKU?.split(',') : [];
        //console.log('apiskus', apiSkus);
        if (prodKkInfo?.AdditionalSKUTvBB && isTVBB) {
          apiSkus = [...apiSkus, prodKkInfo?.AdditionalSKUTvBB];
        }
        if (prodKkInfo?.Modem && !isByod) {
          apiSkus = [...apiSkus, { modem: prodKkInfo?.Modem?.split(',') }];
        }
        if (prodKkInfo?.Mesh) {
          apiSkus = [...apiSkus, { mesh: prodKkInfo?.Mesh?.split(',') }];
        }
      }
      if (product?.productSku?.[0] === MODAL_DEVICE_INFO_TYPE.STATIC_IP) {
        apiSkus = [
          ...apiSkus,
          {
            static_ip: product?.productSku,
          },
        ];
      }
      let sku: any = [];
      if (
        product?.productSku &&
        product?.productSku?.[0] &&
        product?.productSku?.[0] !== MODAL_DEVICE_INFO_TYPE.SKY_ROUTER &&
        product?.productSku?.[0] !== MODAL_DEVICE_INFO_TYPE.STATIC_IP &&
        product?.productSku?.[0] !== MODAL_DEVICE_INFO_TYPE.SKY_BOOSTER
      ) {
        if (!addSkus.includes(product?.productSku[0])) {
          sku = [...sku, ...(product?.productSku ?? [])];
        }
      } else if (product?.sku) {
        if (!addSkus.includes(product?.sku)) sku = [...sku, product?.sku];
      }
      addSkus = [...addSkus, ...sku, ...apiSkus];
    });

  // Remove duplicates code
  const seen = new Set();
  addSkus = addSkus.filter(item => {
    const key = typeof item === 'object' ? JSON.stringify(item) : item;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return { addSkus, installationType };
};

export const getCategory = (category: string | undefined) => {
  if (!category) return '';

  if (category.toLocaleLowerCase() === CATEGORY.TV.toLocaleLowerCase()) {
    return CATEGORY.TV.toLocaleLowerCase();
  } else if (category.toLocaleLowerCase() === CATEGORY.BROADBAND.toLocaleLowerCase()) {
    return CATEGORY.BROADBAND.toLocaleLowerCase();
  } else if (category.toLocaleLowerCase() === CATEGORY.TV_BROADBAND.toLocaleLowerCase()) {
    return `${CATEGORY.TV.toLocaleLowerCase()}-${CATEGORY.BROADBAND.toLocaleLowerCase()}`;
  }
  return '';
};

export const getVoucherProductType = (category: string | undefined) => {
  if (!category) return '';

  if (category.toLocaleLowerCase() === CATEGORY.TV.toLocaleLowerCase()) {
    return VoucherProductTypeEnums.DTH;
  } else if (category.toLocaleLowerCase() === CATEGORY.BROADBAND.toLocaleLowerCase()) {
    return VoucherProductTypeEnums.BROADBAND;
  } else if (category.toLocaleLowerCase() === CATEGORY.TV_BROADBAND.toLocaleLowerCase()) {
    return VoucherProductTypeEnums.DTH_AND_BROADBAND;
  }
  return '';
};

export const getJouneryTypeBasedOnProductType = (productType: string) => {
  if (!productType) return '';

  if (productType.toLocaleLowerCase() === VoucherProductTypeEnums.DTH.toLocaleLowerCase()) {
    return CATEGORY.TV.toLowerCase();
  } else if (
    productType.toLocaleLowerCase() === VoucherProductTypeEnums.BROADBAND.toLocaleLowerCase()
  ) {
    return CATEGORY.BROADBAND.toLowerCase();
  } else if (
    productType.toLocaleLowerCase() ===
    VoucherProductTypeEnums.DTH_AND_BROADBAND.toLocaleLowerCase()
  ) {
    return CATEGORY.TV_BROADBAND.toLowerCase();
  }
  return productType;
};
